import React, { useEffect, useState } from 'react';
import SearchableSelect from '../SearchableSelect';
import GoalOptions from '../GoalOptions';

const CampaignSettings = ({ formData, updateFormData, setIsFormValid }) => {
  const [senderIdOptions, setSenderIdOptions] = useState([]);
  const segmentGeos =
    formData.segmentGeos && formData.segmentGeos.length
      ? formData.segmentGeos
      : formData.segmentGeo
      ? [formData.segmentGeo]
      : [];
  const geoKey = segmentGeos.join(',');
  const isMultiGeo = segmentGeos.length > 1;
  const coveredGeos = Array.from(new Set(senderIdOptions.map((s) => s.geo)));
  const uncoveredGeos = segmentGeos.filter((g) => !coveredGeos.includes(g));
  const [loadingSenders, setLoadingSenders] = useState(false);
  const [destinationUrl, setDestinationUrl] = useState(
    () => formData.destinationUrl || ''
  );
  const [rebrandlyData, setRebrandlyData] = useState(() => {
    if (formData.shortUrl) {
      return {
        shortUrl: formData.shortUrl,
        id: formData.rebrandlyId,
        slashtag: formData.slashtag,
        destination: formData.destinationUrl,
        createdAt: formData.linkCreatedAt,
      };
    }
    return null;
  });
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [isDeletingLink, setIsDeletingLink] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [showCopied, setShowCopied] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState(false);
  const [showCautious, setShowCautious] = useState(!!formData.cautiousSendingEnabled);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    updateFormData({
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleInputChange = (name, value) => {
    updateFormData({
      [name]: value,
    });
  };

  useEffect(() => {
    const incoming = formData.destinationUrl || '';
    if (incoming === destinationUrl) return;

    setDestinationUrl(incoming);
    if (incoming) {
      const { isValid, error } = validateUrl(incoming);
      setIsValidUrl(isValid);
      setUrlError(error);
    } else {
      setIsValidUrl(false);
      setUrlError('');
    }
  }, [formData.destinationUrl]);

  // Ссылку может создать и шаг «Message» при применении шаблона — подхватываем её,
  // иначе поле остаётся в режиме «сократить», а цель Click заблокирована.
  useEffect(() => {
    if ((formData.rebrandlyId || null) === (rebrandlyData?.id || null)) return;

    setRebrandlyData(
      formData.shortUrl
        ? {
            shortUrl: formData.shortUrl,
            id: formData.rebrandlyId,
            slashtag: formData.slashtag,
            destination: formData.destinationUrl,
            createdAt: formData.linkCreatedAt,
          }
        : null
    );
  }, [formData.rebrandlyId, formData.shortUrl]);

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setDestinationUrl(url);

    if (url) {
      const { isValid, error } = validateUrl(url);
      setIsValidUrl(isValid);
      setUrlError(error);
    } else {
      setIsValidUrl(false);
      setUrlError('');
    }
  };

  // Sender ID выбирать вручную не обязательно: по умолчанию маршрут считается
  // по приоритетам ГЕО получателя. Запуск блокируем, только если слать нечем
  // вообще — ни для одной страны сегмента нет связки. Частичное покрытие даёт
  // предупреждение: остальные ГЕО уйдут нормально.
  // Ссылка Rebrandly НЕ обязательна: кампанию можно запускать и без короткой ссылки.
  useEffect(() => {
    const isValid = !!(
      formData.name?.trim() &&
      segmentGeos.length > 0 &&
      coveredGeos.length > 0
    );
    setIsFormValid(isValid);
  }, [formData.name, geoKey, coveredGeos.length, setIsFormValid]);

  useEffect(() => {
    if (isMultiGeo && formData.senderIdId) {
      updateFormData({ senderIdId: null });
    }
  }, [isMultiGeo, formData.senderIdId]);

  // Цель определяется наличием ссылки: без короткой ссылки клики измерить нечем,
  // поэтому цель 'click' доступна только когда ссылка создана. Нет ссылки → deposit.
  useEffect(() => {
    if (!rebrandlyData && formData.goalType === 'click') {
      updateFormData({ goalType: 'deposit' });
    }
  }, [rebrandlyData, formData.goalType]);

  useEffect(() => {
    if (rebrandlyData) return;
    if (formData.ctrThreshold == null && formData.cpcThreshold == null) return;

    updateFormData({ ctrThreshold: null, cpcThreshold: null });
  }, [rebrandlyData, formData.ctrThreshold, formData.cpcThreshold]);

  useEffect(() => {
    if (!geoKey) {
      setSenderIdOptions([]);
      return;
    }
    setLoadingSenders(true);
    fetch(`/api/v1/sender_ids?geos=${encodeURIComponent(geoKey)}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setSenderIdOptions(data))
      .catch(() => setSenderIdOptions([]))
      .finally(() => setLoadingSenders(false));
  }, [geoKey]);

  // Функция для валидации URL
  const validateUrl = (url) => {
    try {
      // Убираем пробелы
      const trimmedUrl = url.trim();
      if (trimmedUrl !== url) {
        return {
          isValid: false,
          error: 'URL should not contain leading or trailing spaces',
        };
      }
      if (url.includes(' ')) {
        return { isValid: false, error: 'URL should not contain spaces' };
      }

      // Проверяем что это валидный URL
      const urlObject = new URL(url);
      // Проверяем что протокол http или https
      if (!['http:', 'https:'].includes(urlObject.protocol)) {
        return {
          isValid: false,
          error: 'URL must start with http:// or https://',
        };
      }

      return { isValid: true, error: '' };
    } catch (e) {
      return { isValid: false, error: 'Please enter a valid URL' };
    }
  };

  const createRebrandlyLink = async () => {
    if (!destinationUrl) return;

    if (!formData.name?.trim()) {
      setLinkError('Please enter a campaign name first');
      return;
    }

    if (!destinationUrl || !isValidUrl) {
      setLinkError('Please enter a valid destination URL');
      return;
    }

    setIsCreatingLink(true);
    setLinkError('');

    try {
      const response = await fetch('/api/v1/rebrandly/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          destination_url: destinationUrl,
          title: formData.name,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create link');
      }

      setRebrandlyData(data);
      updateFormData({
        shortUrl: data.shortUrl,
        rebrandlyId: data.id,
        slashtag: data.slashtag,
        fullShortUrl: `https://${data.shortUrl}`,
        destinationUrl: destinationUrl,
        linkCreatedAt: data.createdAt,
      });
    } catch (error) {
      setLinkError(error.message);
    } finally {
      setIsCreatingLink(false);
    }
  };

  const deleteAndCreateNewLink = async () => {
    if (!rebrandlyData?.id) return;

    setIsDeletingLink(true);
    setLinkError('');

    try {
      const response = await fetch(`/api/v1/rebrandly/${rebrandlyData.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete link');
      }

      // Очищаем данные после успешного удаления
      setRebrandlyData(null);
      setDestinationUrl('');
      updateFormData({
        shortUrl: null,
        rebrandlyId: null,
        slashtag: null,
        fullShortUrl: null,
        destinationUrl: null,
        linkCreatedAt: null,
        ctrThreshold: null,
        cpcThreshold: null,
      });

      setDeleteSuccessMessage(true);
      setTimeout(() => setDeleteSuccessMessage(false), 5000);
    } catch (error) {
      setLinkError(error.message);
    } finally {
      setIsDeletingLink(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`https://${rebrandlyData.shortUrl}`);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className='space-y-6'>
      {/* Campaign Name */}
      <div>
        <label
          className='block text-gray-700 text-sm font-bold mb-2'
          htmlFor='name'
        >
          Campaign Name *
        </label>
        <input
          id='name'
          name='name'
          type='text'
          value={formData.name}
          onChange={handleChange}
          className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
          placeholder='Enter campaign name'
        />
      </div>

      <div>
        <label
          className='block text-gray-700 text-sm font-bold mb-2'
          htmlFor='senderId'
        >
          Sender ID
        </label>
        <SearchableSelect
          options={[
            { value: '', label: 'Auto — route by GEO and priority (recommended)' },
            ...senderIdOptions.map((s) => ({
              value: s.id,
              label: `${s.value} — ${s.provider_name} (${s.geo}, priority ${s.priority})`,
            })),
          ]}
          value={isMultiGeo ? '' : formData.senderIdId || ''}
          onChange={(v) => handleInputChange('senderIdId', v || null)}
          isDisabled={!geoKey || loadingSenders || isMultiGeo}
          placeholder={
            !geoKey
              ? 'Select a segment first'
              : loadingSenders
              ? 'Loading…'
              : isMultiGeo
              ? 'Auto — routed per player GEO'
              : `No Sender IDs for GEO ${segmentGeos[0]}`
          }
        />

        {isMultiGeo && (
          <p className='mt-1 text-xs text-gray-500'>
            This segment holds players from {segmentGeos.length} countries, so every message is
            routed by the player's own GEO. Pinning a single Sender ID is not available.
          </p>
        )}

        {!isMultiGeo && formData.senderIdId && (
          <p className='mt-2 text-xs text-gray-500'>
            Pinned to one binding. Backups of lower priority still take over on gateway errors.
          </p>
        )}

        {!formData.senderIdId && coveredGeos.length > 0 && (
          <div className='mt-2 space-y-1 text-xs text-gray-500'>
            {segmentGeos
              .filter((geo) => coveredGeos.includes(geo))
              .map((geo) => {
                const forGeo = senderIdOptions.filter((s) => s.geo === geo);
                const primary = forGeo.filter((s) => s.primary);
                const backup = forGeo.filter((s) => !s.primary);
                return (
                  <p key={geo}>
                    <span className='font-medium text-gray-600'>{geo}:</span>{' '}
                    {primary.map((s) => `${s.value} (${s.traffic_share}%)`).join(' + ')}
                    {backup.length > 0 &&
                      `, backup: ${backup
                        .map((s) => `${s.value} (p${s.priority})`)
                        .join(', ')}`}
                  </p>
                );
              })}
          </div>
        )}

        {!loadingSenders && uncoveredGeos.length > 0 && (
          <p
            className={`mt-1 text-xs ${
              coveredGeos.length > 0 ? 'text-amber-600' : 'text-red-600'
            }`}
          >
            No Sender ID is configured for {uncoveredGeos.join(', ')}.{' '}
            {coveredGeos.length > 0
              ? 'Players from those countries will fail — add a binding on the Sender IDs page.'
              : 'Add one on the Sender IDs page before sending.'}
          </p>
        )}
      </div>

      {/* Rebrandly URL Creation */}
      <div>
        <label className='block text-gray-700 text-sm font-bold mb-2'>
          Destination URL for Rebrandly{' '}
          <span className='font-normal text-gray-400'>(optional)</span>
        </label>
        <div className='flex space-x-2'>
          <input
            type='url'
            value={destinationUrl}
            onChange={handleUrlChange}
            className={`flex-1 shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${
              urlError ? 'border-red-500' : ''
            }`}
            placeholder='Enter destination URL (e.g., https://example.com)'
            disabled={!!rebrandlyData}
          />
          <button
            onClick={createRebrandlyLink}
            disabled={
              !destinationUrl ||
              !isValidUrl ||
              isCreatingLink ||
              !!rebrandlyData
            }
            className={`px-4 py-2 rounded font-semibold text-white focus:outline-none ${
              !destinationUrl ||
              !isValidUrl ||
              isCreatingLink ||
              !!rebrandlyData
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {isCreatingLink ? (
              <span className='flex items-center'>
                <svg
                  className='animate-spin -ml-1 mr-2 h-4 w-4'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  />
                </svg>
                Creating...
              </span>
            ) : (
              'Create'
            )}
          </button>
        </div>
        {urlError && <p className='mt-1 text-sm text-red-600'>{urlError}</p>}
        {linkError && <p className='mt-2 text-sm text-red-600'>{linkError}</p>}
        {rebrandlyData && (
          <div className='mt-4'>
            <div className='bg-gray-50 p-4 rounded-lg border border-gray-200'>
              <div className='flex items-center justify-between'>
                <div className='space-y-1'>
                  <p className='text-sm text-gray-500'>Short URL:</p>
                  <p className='text-base font-medium text-gray-900'>
                    https://{rebrandlyData.shortUrl}
                  </p>
                </div>
                <button
                  onClick={copyToClipboard}
                  className='p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors'
                  title='Copy to clipboard'
                >
                  {showCopied ? (
                    <span className='text-green-600 flex items-center'>
                      <svg
                        className='w-5 h-5 mr-1'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth='2'
                          d='M5 13l4 4L19 7'
                        />
                      </svg>
                      Copied!
                    </span>
                  ) : (
                    <svg
                      className='w-5 h-5'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2'
                      />
                    </svg>
                  )}
                </button>
              </div>
              <div className='mt-2 text-sm text-gray-500'>
                <p>
                  Destination: {rebrandlyData.destination || destinationUrl}
                </p>
                <p>
                  Created: {new Date(rebrandlyData.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
            <button
              onClick={deleteAndCreateNewLink}
              disabled={isDeletingLink}
              className={`mt-2 text-sm text-red-600 hover:text-red-800 flex items-center ${
                isDeletingLink ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isDeletingLink ? (
                <>
                  <svg
                    className='animate-spin -ml-1 mr-2 h-4 w-4'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  Deleting...
                </>
              ) : (
                'Clear and create new link'
              )}
            </button>
          </div>
        )}
        {deleteSuccessMessage && (
          <div className='mt-2 p-3 bg-green-50 border border-green-200 rounded-lg'>
            <div className='flex items-center text-green-600'>
              <svg
                className='w-5 h-5 mr-2'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M5 13l4 4L19 7'
                />
              </svg>
              <span>
                Link successfully deleted. You can now create a new one.
              </span>
            </div>
          </div>
        )}
      </div>

      <div className='grid grid-cols-2 gap-4'>
        <div>
          <label
            className='block text-gray-700 text-sm font-bold mb-2'
            htmlFor='goalType'
          >
            Campaign Goal
          </label>
          <select
            id='goalType'
            name='goalType'
            value={formData.goalType}
            onChange={handleChange}
            className='shadow border rounded w-full py-2 px-3 text-gray-700 bg-white leading-tight focus:outline-none focus:shadow-outline'
          >
            <GoalOptions disableClick={!rebrandlyData} />
          </select>
          {!rebrandlyData && (
            <p className='mt-1 text-xs text-gray-500'>
              Create a short link above to enable the click goal.
            </p>
          )}
        </div>
        <div>
          <label
            className='block text-gray-700 text-sm font-bold mb-2'
            htmlFor='attributionWindowHours'
          >
            Attribution window (hours)
          </label>
          <select
            id='attributionWindowHours'
            name='attributionWindowHours'
            value={formData.attributionWindowHours}
            onChange={handleChange}
            className='shadow border rounded w-full py-2 px-3 text-gray-700 bg-white leading-tight focus:outline-none focus:shadow-outline'
          >
            {[1, 2, 3, 4, 5, 6].map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className='border border-gray-200 rounded-lg p-4'>
        <div className='flex items-center justify-between'>
          <div>
            <p className='text-gray-700 text-sm font-bold'>Cautious sending</p>
            <p className='text-xs text-gray-500'>
              Send a small test batch first, wait, and only continue if CTR and CPC hold up.
            </p>
          </div>
          <button
            type='button'
            onClick={() => {
              const next = !showCautious;
              setShowCautious(next);
              updateFormData({ cautiousSendingEnabled: next });
            }}
            className={`px-3 py-1.5 text-sm rounded ${
              showCautious
                ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {showCautious ? 'Disable' : 'Enable'}
          </button>
        </div>

        {showCautious && (
          <div className='mt-4 grid grid-cols-2 gap-4'>
            <div>
              <label className='block text-gray-700 text-xs font-bold mb-1'>
                Test batch (% of audience)
              </label>
              <input
                type='number'
                min='1'
                max='100'
                name='batchPercent'
                value={formData.batchPercent}
                onChange={handleChange}
                className='shadow border rounded w-full py-2 px-3 text-gray-700'
              />
            </div>
            <div>
              <label className='block text-gray-700 text-xs font-bold mb-1'>Wait time (minutes)</label>
              <input
                type='number'
                min='1'
                name='waitTimeMinutes'
                value={formData.waitTimeMinutes}
                onChange={handleChange}
                className='shadow border rounded w-full py-2 px-3 text-gray-700'
              />
            </div>
            <div>
              <label className='block text-gray-700 text-xs font-bold mb-1'>CTR threshold (%)</label>
              <input
                type='number'
                min='0'
                step='0.01'
                name='ctrThreshold'
                value={formData.ctrThreshold ?? ''}
                onChange={handleChange}
                disabled={!rebrandlyData}
                placeholder={rebrandlyData ? 'no limit' : 'needs a short link'}
                className={`shadow border rounded w-full py-2 px-3 text-gray-700 ${
                  !rebrandlyData ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              />
            </div>
            <div>
              <label className='block text-gray-700 text-xs font-bold mb-1'>CPC threshold ($)</label>
              <input
                type='number'
                min='0'
                step='0.0001'
                name='cpcThreshold'
                value={formData.cpcThreshold ?? ''}
                onChange={handleChange}
                disabled={!rebrandlyData}
                placeholder={rebrandlyData ? 'no limit' : 'needs a short link'}
                className={`shadow border rounded w-full py-2 px-3 text-gray-700 ${
                  !rebrandlyData ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
              />
            </div>
            {rebrandlyData ? (
              <p className='col-span-2 text-xs text-gray-500'>
                CPC uses the price per SMS set on the gateway that actually delivered each message.
                Leave a threshold empty to skip that check.
              </p>
            ) : (
              <p className='col-span-2 text-xs text-amber-700 bg-amber-50 rounded px-3 py-2'>
                Both thresholds are measured by clicks on the short link — create one above to use
                them. Without a link the test batch still goes out, but there is nothing to check
                against, so the campaign will wait for your decision.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Send Time Options */}
      <div>
        <label className='block text-gray-700 text-sm font-bold mb-2'>
          Send Time
        </label>
        <div className='space-y-4'>
          <div className='flex items-center'>
            <input
              type='radio'
              id='sendNow'
              name='sendTime'
              value='now'
              checked={formData.sendTime === 'now'}
              onChange={handleChange}
              className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300'
            />
            <label htmlFor='sendNow' className='ml-2 block text-gray-700'>
              Send Now
            </label>
          </div>

          <div className='flex items-center'>
            <input
              type='radio'
              id='sendLater'
              name='sendTime'
              value='later'
              checked={formData.sendTime === 'later'}
              onChange={handleChange}
              className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300'
            />
            <label htmlFor='sendLater' className='ml-2 block text-gray-700'>
              Schedule for Later
            </label>
          </div>

          {formData.sendTime === 'later' && (
            <div className='ml-6'>
              <input
                type='datetime-local'
                name='scheduledTime'
                value={formData.scheduledTime}
                onChange={handleChange}
                className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignSettings;
